/**
  * @class mmodOfficeWfTmDevices
  * @description
  *
  * # Purpose
  * mOffice module for the SK register of measuring devices as IS TM knows them
  * (serial number → device GUID). The list/filter/detail views come from the server
  * workflow (`msvrSkWfTmDevices`); on top of the generic module this adds the bulk
  * CSV import of the GUID map:
  *
  *   - an "Import CSV" button next to the New button opens a file picker
  *   - a CSV file dropped anywhere in the module arrives via `mmFilesDropped`
  *     (forwarded by mmodOfficeImport) and imports the same way
  *
  * The file format is one `id;guid[;note]` line per device (a header line is skipped).
  * There is deliberately NO custom server message: every row is a plain workflow
  * `mmAction` - NEW for unknown serials, UPDATE for changed ones, RESTORE+UPDATE for
  * deleted ones - so the ACLs apply to the importing operator, each change is logged
  * under their identity, every watcher receives the rows as live pushes, and rows
  * already matching the register are skipped (re-importing the same file is idempotent).
  */

import mmodWorkflow from 'mmodWorkflow'

export default class mmodOfficeWfTmDevices extends mmodWorkflow {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([], dependencies)
    cfg_schema = _.extend({
      import_type: {
        type: 'string',
        description: 'device type stamped on records created by the bulk CSV import',
        default: 'trucam',
      },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * After the model arrives the filter bar exists - add the Import CSV button next to New.
  */
  async mmModel(sender, params) {
    await super.mmModel(sender, params)
    this.addImportButton()
  } // mmModel

  addImportButton() {
    const elFilter = this.elFilter
    if(!elFilter || elFilter.find('[key=import_csv]')) return
    const elNew = elFilter.find('[action=new]')
    const parent = elNew ? elNew.parentNode : elFilter

    // hidden file picker; drag&drop anywhere in the module is handled centrally by
    // mmodOfficeImport, which forwards the file here as mmFilesDropped
    const elPicker = document.createElement('input')
    elPicker.type = 'file'
    elPicker.accept = '.csv,text/csv,text/plain'
    elPicker.style.display = 'none'
    parent.appendChild(elPicker)
    elPicker.onchange = ()=>{
      const file = elPicker.files[0]
      elPicker.value = ''
      if(file) this.importFile(file)
    }

    const elButton = parent.appendChildren({
      tag: 'mele-button',
      key: 'import_csv',
      type: elNew ? elNew.getAttribute('type') || 'actionbar' : 'actionbar',
      iconleft: 'server.upload.file',
      label: `{{${this.tr('import.Button')}}}`,
    })
    if(elNew) parent.insertBefore(elButton, elNew)
    elButton.onclick = ()=>elPicker.click()
  } // addImportButton

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * CSV file dropped anywhere in this module, forwarded by mmodOfficeImport.
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {Array<{name: string, mime: string, data: string}>} params.files - dropped files
  */
  async mmFilesDropped(sender, params) {
    for(const file of (Array.isArray(params?.files) ? params.files : [])) {
      if(!/\.(csv|txt)$/i.test(_.toString(file.name))) continue
      const text = this.decodeDataUrl(_.toString(file.data))
      if(text) return this.importCsv(text) // one file per drop - the GUID map is one document
    }
  } // mmFilesDropped

  importFile(file) {
    const reader = new FileReader()
    reader.onload = ()=>this.importCsv(_.toString(reader.result))
    reader.readAsText(file)
  } // importFile

  decodeDataUrl(data_url) {
    const comma = data_url.indexOf(',')
    if(comma<0) return data_url // already plain text
    const meta = data_url.slice(0, comma)
    const body = data_url.slice(comma + 1)
    try{
      if(/;base64/i.test(meta)) {
        return new TextDecoder('utf-8').decode(Uint8Array.from(atob(body), c=>c.charCodeAt(0)))
      }
      return decodeURIComponent(body)
    }catch(e) {
      return ''
    }
  } // decodeDataUrl

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Parses the CSV client-side and drives one plain workflow mmAction per row. The module
  * watches the whole register, so the branching (new/changed/deleted/unchanged) reads from
  * the live list; the action echoes and watch pushes keep it current as the import runs.
  * @param {string} text - CSV file content (`id;guid[;note]` lines)
  */
  async importCsv(text) {
    const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
    const lines = _.toString(text).split(/\r?\n/)
    const summary = { created: 0, updated: 0, restored: 0, unchanged: 0, errors: [] }

    const rows = []
    for(let i = 0; i<lines.length; i++) {
      const line = lines[i].trim()
      if(line==='') continue
      const cells = line.split(';').map(cell=>cell.trim())
      const id = _.toString(cells[0]).replace(/\s+/g, '')
      const guid = _.toString(cells[1]).toLowerCase()
      const note = _.toString(cells[2])
      if(!GUID_RE.test(guid)) {
        if(i>0) summary.errors.push(`${i + 1}: "${cells[1] || ''}"`) // line 1 may be the header
        continue
      }
      if(!/^[\w-]{3,32}$/.test(id)) {
        summary.errors.push(`${i + 1}: "${cells[0] || ''}"`)
        continue
      }
      rows.push({ line: i + 1, id, guid, note })
    }
    if(rows.length===0) {
      return this.modalError(`{{${this.tr('import.Invalid')}}}`, `{{${this.tr('import.Title')}}}`)
    }

    this.modalWait('{{Please wait}}...', `{{${this.tr('import.Title')}}}`)
    const action = async (id, action_name, params)=>{
      await this.request(this.config.workflow_server, 'mmAction', { id, action: action_name, params }, ['mmSuccess', 'mmObjects'])
    }
    for(const row of rows) {
      const desired = { type: this.config.import_type, guid: row.guid, note: row.note }
      const existing = _.get(this.elList?.getItem(row.id), 'data')
      try{
        if(!existing) {
          await action(row.id, 'NEW', desired)
          summary.created++
        }else if(existing.state==='DELETED') {
          await action(row.id, 'RESTORE', {})
          await action(row.id, 'UPDATE', desired)
          summary.restored++
        }else if(existing.guid===row.guid && _.toString(existing.note)===row.note && existing.type===desired.type) {
          summary.unchanged++
        }else{
          await action(row.id, 'UPDATE', desired)
          summary.updated++
        }
      }catch(e) {
        summary.errors.push(`${row.line}: ${row.id} - ${_.toString(e?.params?.error || e?.message || e)}`)
      }
    }
    this.modalCloseDefault()

    let message = [
      `${summary.created} {{${this.tr('import.Created')}}}`,
      `${summary.updated} {{${this.tr('import.Updated')}}}`,
      `${summary.restored} {{${this.tr('import.Restored')}}}`,
      `${summary.unchanged} {{${this.tr('import.Unchanged')}}}`,
    ].join(', ')
    if(summary.errors.length>0) {
      message += `\n\n${summary.errors.length} {{${this.tr('import.Errors')}}}:\n` + summary.errors.slice(0, 12).join('\n')
      if(summary.errors.length>12) message += '\n…'
      this.modalError(message, `{{${this.tr('import.Done')}}}`)
    }else{
      this.modalInfo(message, `{{${this.tr('import.Done')}}}`)
    }
  } // importCsv
} // mmodOfficeWfTmDevices
